/* eslint-disable prettier/prettier */

import { AxiosError } from 'axios';
import { Api } from 'api';
import i18n from 'i18n';
import { TOKENS } from 'constants/index';
import {
  ApiCallHistory,
  ApiUser,
  ApiWaitingRoomLinkProfile,
  ClinicListResponse,
  GetStaffRequest,
  StaffResponse,
  InvitationInfoResponse,
  InviteRequest,
  LoginRequest,
  LoginResponse,
  PasswordUpdateRequest,
  RefreshTokenRequest,
  RegisterByInvitationRequest,
  RegisterRequest,
  ResetPasswordRequest,
  TokenResponse,
  TwoFactor,
  UpdateCheckInNotificationPreferencesRequest,
  UpdateClinicRequest,
  UpdateClinicUserRequest,
  UpdateUserRequest,
  GetPlanResponse,
  LicenseUsageInfoResponse,
  SubscriptionsApiResponse,
  CreateHostedPageRequest,
  CreateHostedPageResponse,
  ReactivateCurrentSubscriptionApiResponse,
  UpdateCustomerCurrentCardRequest,
  UpdateCustomerCurrentCardResponse,
  UpdateSubscriptionWithIncreasingAmountOfLicensesRequest,
  UpdateSubscriptionWithDecreasingAmountOfLicensesRequest,
  UpdateSubscriptionWithIncreasingAmountOfLicensesResponse,
  ManageLicenseRequest,
  LoginWithVerificationCodeRequest,
  GetCouponSubscriptionApiResponse,
  ComputeSubscriptionProrationRequest,
  ComputeSubscriptionProrationResponse,
  CreateBaaRequest,
  PostCheckoutSubscriptionsApiResponse,
  GetSubscriptionInvoicesResponse,
  InviteToCallBySmsRequest,
  InviteToCallByEmailRequest,
  AllowDisablePaymentsRequest,
  CreatePaymentRequest,
  GetAllAINotesPurchaseItemsResponse,
  MakePurchaseResponse,
  AINotesPreselectsResponse,
  GetAINotesResponse,
  ResetStaffPasswordRequest,
} from 'types';
import {
  DocumentWithMetadataDto,
  UpdateFolderPermissionsRequest,
  GetFolderPermissionsResponse,
  FolderMemberPermissionDto,
  FolderAccessLevel,
} from 'types/document';
import { FaxAccountDto } from 'types/fax';
import { CustomConsentFormSummary, CustomConsentFormDetail, UpdateCustomConsentFormPayload } from 'types/teleconsent';

export interface IApiRequests {
  register(registerRequest: RegisterRequest): Promise<void>;
  login(loginRequest: LoginRequest): Promise<LoginResponse>;
  loginWithVerificationCode(payload: LoginWithVerificationCodeRequest): Promise<LoginResponse>;
  refreshToken(refreshTokenRequest: RefreshTokenRequest): Promise<TokenResponse>;
  logout(): Promise<void>;
  registerSecondAccount(organization: string): Promise<TokenResponse>;
  confirmEmail(userId: string, code: string): Promise<TokenResponse>;
  resendConfirmEmail(email: string): Promise<void>;
  sendResetPassword(email: string): Promise<void>;
  resetPassword(resetPasswordRequest: ResetPasswordRequest): Promise<void>;
  updateUserPassword(data: PasswordUpdateRequest): Promise<void>;
  registerUserByInvitation(metadata: RegisterByInvitationRequest): Promise<TokenResponse>;

  updateCallHistory({ room, note }: { room: string; note: string }): Promise<void>;
  endCall(room: string, endedAtUtc?: string): Promise<void>;
  createRoom(forceGroup: boolean): Promise<{ room: string; isGroup: boolean }>;
  getRoomHistory(room: string): Promise<ApiCallHistory>;
  getDiagnosticsToken(): Promise<{
    token: string;
    iceServers: { url: string; username: string; credential: string }[];
  }>;
  speedTestDownload(): Promise<{ bytes: number; durationMs: number }>;
  speedTestUpload(sizeBytes: number): Promise<{ durationMs: number }>;

  updateClinic(updateClinicRequest: UpdateClinicRequest): Promise<void>;
  updateClinicPicture(file: File | null): Promise<void>;
  getClinicsList(): Promise<ClinicListResponse>;
  changeClinic(clinicId: string): Promise<TokenResponse>;

  updateClinicUser(updateClinicUserRequest: UpdateClinicUserRequest): Promise<void>;
  updateCheckInNotificationPreferences(payload: UpdateCheckInNotificationPreferencesRequest): Promise<void>;
  getProfileByLink(roomName: string): Promise<ApiWaitingRoomLinkProfile>;
  skipCreateRoom(): Promise<void>;

  ensureRoomNameIsUnique(roomName: string): Promise<void>;

  invite(inviteRequest: InviteRequest): Promise<string>;
  invitationResend(invitationId: string): Promise<void>;
  invitationDecline(invitationId: string): Promise<void>;
  invitationAccept(invitationId: string): Promise<void>;
  invitationInfo(invitationId: string): Promise<InvitationInfoResponse>;
  getStaff(getStaffRequest: GetStaffRequest): Promise<StaffResponse>;
  removeInvitation(invitationId: string): Promise<void>;
  removeStaff(clinicUserId: string): Promise<void>;
  inviteToCallBySms(inviteToCallBySmsRequest: InviteToCallBySmsRequest): Promise<void>;
  inviteToCallByEmail(inviteToCallByEmailRequest: InviteToCallByEmailRequest): Promise<void>;

  getPlan(planCode: string): Promise<GetPlanResponse>;
  postCheckoutSubscriptionUpdate(hostedPageId: string): Promise<PostCheckoutSubscriptionsApiResponse>;
  updateSubscriptionWithIncreasingAmountOfLicenses(
    payload: UpdateSubscriptionWithIncreasingAmountOfLicensesRequest,
  ): Promise<UpdateSubscriptionWithIncreasingAmountOfLicensesResponse>;
  updateSubscriptionWithDecreasingAmountOfLicenses(
    payload: UpdateSubscriptionWithDecreasingAmountOfLicensesRequest,
  ): Promise<SubscriptionsApiResponse>;
  getLicenseUsageInfo(clinicId: string): Promise<LicenseUsageInfoResponse>;
  cancelCurrentSubscription(): Promise<SubscriptionsApiResponse>;
  createSubscriptionWithHostedPages(request: CreateHostedPageRequest): Promise<CreateHostedPageResponse>;
  updateCustomerCurrentCard(payload: UpdateCustomerCurrentCardRequest): Promise<UpdateCustomerCurrentCardResponse>;
  reactivateCurrentSubscription(): Promise<ReactivateCurrentSubscriptionApiResponse>;
  getCoupon(promocode: string): Promise<GetCouponSubscriptionApiResponse>;
  manageLicense(payload: ManageLicenseRequest): Promise<SubscriptionsApiResponse>;
  computeSubscriptionProration(
    payload: ComputeSubscriptionProrationRequest,
  ): Promise<ComputeSubscriptionProrationResponse>;
  getSubscriptionInvoices(page?: number, perPage?: number, filterBy?: string): Promise<GetSubscriptionInvoicesResponse>;

  updateUser(updateUserRequest: UpdateUserRequest): Promise<void>;
  updateProfilePicture(file: File | null): Promise<void>;
  sendCodeToPhone(twoFactorMode: TwoFactor, languageCode?: string): Promise<void>;
  verifyPhoneCode(code: string): Promise<boolean>;
  checkEmailExists(email: string): Promise<boolean>;
  getUser(): Promise<ApiUser>;
  preUpdatePhone(newPhone: string, twoFactorMode: string, languageCode?: string): Promise<void>;
  updatePhone(newPhone: string, code: string): Promise<void>;
  preUpdateSmsPhone(newPhone: string, twoFactorMode: string, languageCode?: string): Promise<void>;
  updateSmsPhone(newPhone: string, code: string): Promise<void>;
  turnOnTwoFactor(): Promise<void>;
  turnOffTwoFactor(code: string): Promise<void>;
  changeTwoFactor(twoFactor: TwoFactor): Promise<void>;
  createBaa(payload: CreateBaaRequest): Promise<any>;

  connectStripe(): Promise<string>;
  disconnectStripe(): Promise<void>;
  allowDisablePayments(payload: AllowDisablePaymentsRequest): Promise<void>;
  createPayment(payload: CreatePaymentRequest): Promise<void>;
  cancellPaymentByClient(clientConnectionId: string): Promise<void>;
  cancellPaymentByProvider(): Promise<void>;

  listPersonalDocuments: (path: string) => Promise<DocumentWithMetadataDto[]>;
  listPersonalDocumentsWithMetadata(path: string): Promise<DocumentWithMetadataDto[]>;
  listClinicDocuments(path: string): Promise<DocumentWithMetadataDto[]>;
  uploadPersonalDocument(file: File, path: string, overwrite?: boolean): Promise<string>;
  uploadClinicDocument(file: File, path: string, overwrite?: boolean): Promise<string>;
  uploadGuestChatDocument(
    file: File,
    providerId: string,
    connectionId: string,
  ): Promise<{ path: string; fileName: string; size: number }>;
  downloadPersonalDocument(path: string, fileName: string): Promise<void>;
  downloadSharedDocument(path: string, fileName: string): Promise<void>;
  deletePersonalDocument(path: string, fileName: string): Promise<void>;
  renamePersonalDocument(path: string, oldName: string, newName: string): Promise<string>;
  updatePinnedState(path: string, fileName: string, pinned: boolean): Promise<void>;
  markDocumentAsShared(path: string, fileName: string): Promise<void>;
  getBlobPath(clinicId: string, userId: string | null, fileName: string): Promise<{ id: string }>;
  getDirectDownloadUrl(path: string, fileName: string): Promise<string>;

  confirmTermsAndConditions(): Promise<void>;
  getAllAINotesItems(): Promise<GetAllAINotesPurchaseItemsResponse>;
  makePurchase(itemId: string): Promise<MakePurchaseResponse>;
  getAINotesPreselects(): Promise<AINotesPreselectsResponse>;
  startRecording(startRecordData: {
    typeOfVisit: string;
    writtenAs: string;
    generateNote: string;
    includeReductedTranscript: boolean;
  }): Promise<void>;
  stopRecording(): Promise<void>;
  getAINotes(page: number, count: number): Promise<GetAINotesResponse>;
  downloadNote(aiNote: string, isPdf: boolean): Promise<any>;
  processTooShortRecording(aiNote: string): Promise<void>;
  processStoppedNotByUserRecording(aiNote: string): Promise<void>;

  changeUserRole(payload: { clinicUserId: string; via3UserType: 'Staff' | 'Member' }): Promise<void>;

  getSentFaxes(): Promise<any>;
  getReceivedFaxes(): Promise<any>;
  sendFax: (files: File[], faxNumber: string, subject: string) => Promise<void>;
  getFaxAccount(): Promise<FaxAccountDto>;
  downloadReceivedFax(faxId: string): Promise<Blob>;
  downloadSentFax: (faxId: string) => Promise<Blob>;
  deleteReceivedFax(faxDetailsId: string): Promise<void>;
  deleteSentFax(faxId: string): Promise<void>;
  resendSentFax(faxId: string): Promise<void>;
  createClinicFolder: (path: string) => Promise<void>;
  renameClinicFolder(oldPath: string, newPath: string): Promise<void>;
  deleteClinicFolder(path: string): Promise<void>;
  getFolderPermissions(folderId: string): Promise<FolderMemberPermissionDto[]>;
  updateFolderPermissions(payload: UpdateFolderPermissionsRequest): Promise<void>;
  getClinicStorage(): Promise<{ bytes: number; megabytes: number }>;

  getPublicStaff(): Promise<StaffResponse>;

  getCallHistoryPaged(
    startUtc: string,
    endUtc: string,
    hostClinicUserId: string | null,
    offset: number,
    count: number,
  ): Promise<{
    items: any[];
    totalCount: number;
  }>;

  getCallHistoryExport(
    startUtc: string,
    endUtc: string,
    hostClinicUserId: string | null,
    clientTimeZone: string,
  ): Promise<Blob>;
  getCallHistoryHosts(startUtc: string, endUtc: string): Promise<{ hostClinicUserId: string; hostName: string }[]>;
  getCallHistoryMinDate: () => Promise<string | null>;

  resetStaffPassword(model: ResetStaffPasswordRequest): Promise<void>;

  startTeleconsentSession(payload: {
    formKey: string;
    participantIdentity: string | null;
    providerPrefill?: Record<string, string>;
    providerName?: string;
    clientLabel?: string;
  }): Promise<{ sessionId: string }>;
  submitTeleconsentForm(sessionId: string): Promise<{ submittedAt: string; clientIp: string }>;
  getTeleconsentSettings(): Promise<{
    teleconsentEnabled: boolean;
    teleconsentActivateForAllStaff: boolean;
    teleconsentDisabledForms: string[];
    teleconsentTermsAccepted: boolean;
  }>;
  updateTeleconsentSettings(payload: {
    teleconsentEnabled: boolean;
    teleconsentActivateForAllStaff: boolean;
    teleconsentDisabledForms: string[];
    teleconsentTermsAccepted: boolean;
  }): Promise<void>;

  getCustomConsentForms(): Promise<CustomConsentFormSummary[]>;
  getCustomConsentForm(id: string): Promise<CustomConsentFormDetail>;
  createCustomConsentForm(): Promise<CustomConsentFormSummary>;
  updateCustomConsentForm(id: string, payload: UpdateCustomConsentFormPayload): Promise<void>;
  deleteCustomConsentForm(id: string): Promise<void>;

  sendTeleconsentConversionRequest(files: File[], description: string): Promise<void>;
}

export function useApiRequests(): IApiRequests {
  type ApiErrorPayload = {
    errorCode?: unknown;
    message?: unknown;
  };

  const getErrorMessage = (e: AxiosError): string => {
    const payload = e.response?.data as unknown;

    if (typeof payload === 'string' && payload.trim()) {
      return payload;
    }

    if (payload && typeof payload === 'object') {
      const message = (payload as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }

    return e.message || 'Request failed';
  };

  const getErrorCode = (e: AxiosError): string | undefined => {
    const payload = e.response?.data as unknown;

    if (payload && typeof payload === 'object') {
      const errorCode = (payload as ApiErrorPayload).errorCode;
      if (typeof errorCode === 'string' && errorCode.trim()) {
        return errorCode;
      }
    }

    return undefined;
  };

  const toApiError = (e: AxiosError): Error & { errorCode?: string } => {
    const errorCode = getErrorCode(e);
    const fallbackMessage = getErrorMessage(e);
    const genericMessage = i18n.t('GENERIC_ERROR', { ns: 'errors' });

    let localizedMessage = fallbackMessage;

    if (errorCode) {
      const localizationKey = `${errorCode}`;
      if (i18n.exists(localizationKey, { ns: 'errors' })) {
        localizedMessage = i18n.t(localizationKey, { ns: 'errors' });
      }
    }

    const normalizedFallback = String(fallbackMessage || '')
      .trim()
      .toLowerCase();
    const isLikelyTransportMessage =
      !normalizedFallback ||
      normalizedFallback === 'network error' ||
      normalizedFallback === 'request failed' ||
      normalizedFallback.includes('request failed with status code') ||
      normalizedFallback.includes('failed to fetch');

    if (isLikelyTransportMessage) {
      localizedMessage = genericMessage;
    }

    const error = new Error(localizedMessage) as Error & { errorCode?: string };
    if (errorCode) {
      error.errorCode = errorCode;
    }

    return error;
  };

  ////////////////////////////////////////////////////////////
  /// Auth
  ////////////////////////////////////////////////////////////

  async function register(registerRequest: RegisterRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/auth/register',
      method: 'post',
      payload: registerRequest,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function login(payload: LoginRequest): Promise<LoginResponse> {
    return await Api.unauthRequest<LoginResponse>({
      url: '/api/auth/login',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function loginWithVerificationCode(payload: LoginWithVerificationCodeRequest): Promise<LoginResponse> {
    return await Api.unauthRequest<LoginResponse>({
      url: '/api/auth/loginWithVerificationCode',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function refreshToken(refreshTokenRequest: RefreshTokenRequest): Promise<TokenResponse> {
    return await Api.request<TokenResponse>({
      url: '/api/auth/refreshToken',
      method: 'post',
      payload: refreshTokenRequest,
    });
  }

  async function logout(): Promise<void> {
    return await Api.request<void>({
      url: '/api/auth/logout',
      method: 'post',
      payload: {},
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function registerSecondAccount(organization: string): Promise<TokenResponse> {
    return await Api.request<TokenResponse>({
      url: '/api/auth/registerSecondAccount',
      method: 'post',
      payload: { organization },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function confirmEmail(userId: string, token: string): Promise<TokenResponse> {
    return await Api.request<TokenResponse>({
      url: '/api/auth/confirmEmail',
      method: 'post',
      payload: { userId, token },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function resendConfirmEmail(email: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/auth/resendConfirmEmail',
      method: 'put',
      params: { email },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function sendResetPassword(email: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/auth/sendResetPassword',
      method: 'post',
      payload: { email },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function resetPassword(resetPasswordRequest: ResetPasswordRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/auth/resetPassword',
      method: 'post',
      payload: resetPasswordRequest,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateUserPassword(data: PasswordUpdateRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/auth/changePassword',
      method: 'post',
      payload: data,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function registerUserByInvitation(model: RegisterByInvitationRequest): Promise<TokenResponse> {
    return await Api.request<TokenResponse>({
      url: '/api/auth/registerByInvitation',
      method: 'post',
      payload: model,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Teleconsent
  ////////////////////////////////////////////////////////////

  async function startTeleconsentSession(payload: {
    formKey: string;
    participantIdentity: string | null;
    providerPrefill?: Record<string, string>;
    providerName?: string;
    clientLabel?: string;
  }): Promise<{ sessionId: string }> {
    return await Api.request<{ sessionId: string }>({
      url: '/api/Teleconsent/Start',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function submitTeleconsentForm(sessionId: string): Promise<{ submittedAt: string; clientIp: string }> {
    return await Api.request<{ submittedAt: string; clientIp: string }>({
      url: '/api/Teleconsent/Submit',
      method: 'post',
      payload: { sessionId },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getTeleconsentSettings(): Promise<{
    teleconsentEnabled: boolean;
    teleconsentActivateForAllStaff: boolean;
    teleconsentDisabledForms: string[];
    teleconsentTermsAccepted: boolean;
  }> {
    return await Api.request<{
      teleconsentEnabled: boolean;
      teleconsentActivateForAllStaff: boolean;
      teleconsentDisabledForms: string[];
      teleconsentTermsAccepted: boolean;
    }>({
      url: '/api/Teleconsent/Settings',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateTeleconsentSettings(payload: {
    teleconsentEnabled: boolean;
    teleconsentActivateForAllStaff: boolean;
    teleconsentDisabledForms: string[];
    teleconsentTermsAccepted: boolean;
  }): Promise<void> {
    return await Api.request<void>({
      url: '/api/Teleconsent/Settings',
      method: 'put',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getCustomConsentForms(): Promise<CustomConsentFormSummary[]> {
    return await Api.request<CustomConsentFormSummary[]>({
      url: '/api/CustomConsentForm',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getCustomConsentForm(id: string): Promise<CustomConsentFormDetail> {
    return await Api.request<CustomConsentFormDetail>({
      url: `/api/CustomConsentForm/${id}`,
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function createCustomConsentForm(): Promise<CustomConsentFormSummary> {
    return await Api.request<CustomConsentFormSummary>({
      url: '/api/CustomConsentForm',
      method: 'post',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateCustomConsentForm(id: string, payload: UpdateCustomConsentFormPayload): Promise<void> {
    return await Api.request<void>({
      url: `/api/CustomConsentForm/${id}`,
      method: 'put',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function deleteCustomConsentForm(id: string): Promise<void> {
    return await Api.request<void>({
      url: `/api/CustomConsentForm/${id}`,
      method: 'delete',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function sendTeleconsentConversionRequest(files: File[], description: string): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('description', description);
    return await Api.request<void>({
      url: '/api/Teleconsent/ConversionRequest',
      method: 'post',
      payload: formData,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Staff Actions
  ////////////////////////////////////////////////////////////

  async function resetStaffPassword(data: ResetStaffPasswordRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/Staff/ResetPassword',
      method: 'post',
      payload: data,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Call
  ////////////////////////////////////////////////////////////

  async function updateCallHistory({ room, note }: { room: string; note: string }): Promise<void> {
    return await Api.request<void>({
      url: '/api/call/updateNote',
      method: 'post',
      payload: { room, note },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function createRoom(forceGroup: boolean): Promise<{ room: string; isGroup: boolean }> {
    return await Api.request<{ room: string; isGroup: boolean }>({
      url: '/api/call/createRoom',
      method: 'put',
      payload: {
        forceGroup,
      },
    });
  }

  async function endCall(room: string, endedAtUtc?: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/call/endCall',
      method: 'post',
      payload: { room, endedAtUtc },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function getRoomHistory(room: string): Promise<ApiCallHistory> {
    return await Api.request<ApiCallHistory>({
      url: '/api/call/getCallHistory',
      method: 'get',
      params: { room },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function getDiagnosticsToken(): Promise<{
    token: string;
    iceServers: { url: string; username: string; credential: string }[];
  }> {
    return await Api.request<{ token: string; iceServers: { url: string; username: string; credential: string }[] }>({
      url: '/api/diagnostics/token',
      method: 'get',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  function getAuthHeaders(): Record<string, string> {
    const tokensJson = window.localStorage.getItem(TOKENS);
    const token = tokensJson ? (JSON.parse(tokensJson) as { token: string }).token : '';
    return {
      Authorization: `Bearer ${token}`,
      'app-id': '8fdac0f1-55a4-415f-aff0-3243aa3241d0',
    };
  }

  async function speedTestDownload(): Promise<{ bytes: number; durationMs: number }> {
    let firstByteTime: number | null = null;
    const response = await Api.adapter
      .get<ArrayBuffer>('/api/diagnostics/speed-test/download', {
        responseType: 'arraybuffer',
        headers: getAuthHeaders(),
        onDownloadProgress: (e) => {
          if (firstByteTime === null && e.loaded > 0) firstByteTime = performance.now();
        },
      })
      .catch((e: any) => {
        throw toApiError(e);
      });
    const endTime = performance.now();
    return {
      bytes: response.data.byteLength,
      durationMs: endTime - (firstByteTime ?? endTime),
    };
  }

  async function speedTestUpload(sizeBytes: number): Promise<{ durationMs: number }> {
    const body = new Uint8Array(sizeBytes);
    let uploadEndTime: number | null = null;
    const t0 = performance.now();
    await Api.adapter
      .post('/api/diagnostics/speed-test/upload', body, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/octet-stream' },
        onUploadProgress: (e) => {
          if (e.loaded >= sizeBytes) uploadEndTime = performance.now();
        },
      })
      .catch((e: any) => {
        throw toApiError(e);
      });
    return { durationMs: (uploadEndTime ?? performance.now()) - t0 };
  }

  ////////////////////////////////////////////////////////////
  /// Clinic
  ////////////////////////////////////////////////////////////

  async function updateClinic(updateClinicRequest: UpdateClinicRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/clinic/updateClinic',
      method: 'put',
      payload: updateClinicRequest,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function updateClinicPicture(file: File | null): Promise<void> {
    return await Api.request<void>({
      url: '/api/clinic/updateClinicPicture',
      method: 'put',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      payload: { file },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getClinicsList(): Promise<ClinicListResponse> {
    return await Api.request<ClinicListResponse>({
      url: '/api/clinic/userClinics',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function changeClinic(clinicId: string): Promise<TokenResponse> {
    return await Api.request<TokenResponse>({
      url: '/api/clinic/changeClinic',
      method: 'put',
      payload: { clinicId },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Clinic User
  ////////////////////////////////////////////////////////////

  async function updateClinicUser(updateClinicUserRequest: UpdateClinicUserRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/clinicUser/updateClinicUser',
      method: 'put',
      payload: updateClinicUserRequest,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function updateCheckInNotificationPreferences(
    payload: UpdateCheckInNotificationPreferencesRequest,
  ): Promise<void> {
    return await Api.request<void>({
      url: '/api/clinicUser/updateCheckInNotificationPreferences',
      method: 'put',
      payload,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function getProfileByLink(roomName: string): Promise<ApiWaitingRoomLinkProfile> {
    return await Api.request<ApiWaitingRoomLinkProfile>({
      url: '/api/clinicUser/getByRoomName',
      method: 'get',
      params: { roomName },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function skipCreateRoom(): Promise<void> {
    return await Api.request<void>({
      url: '/api/clinicUser/skipCreateRoom',
      method: 'put',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function ensureRoomNameIsUnique(roomName: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/clinicUser/ensureRoomNameIsUnique',
      method: 'get',
      params: { roomName },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Invitation
  ////////////////////////////////////////////////////////////

  async function invite(inviteRequest: InviteRequest): Promise<string> {
    return await Api.request<string>({
      url: '/api/invitation/invite',
      method: 'put',
      payload: inviteRequest,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function invitationResend(invitationId: string): Promise<void> {
    await Api.request<string>({
      url: '/api/invitation/resendInvite',
      method: 'put',
      params: { invitationId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function invitationDecline(invitationId: string): Promise<void> {
    await Api.request<string>({
      url: '/api/invitation/declineInvitation',
      method: 'put',
      params: { invitationId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function invitationAccept(invitationId: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/invitation/acceptInvitation',
      method: 'put',
      params: { invitationId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function invitationInfo(invitationId: string): Promise<InvitationInfoResponse> {
    return await Api.request<InvitationInfoResponse>({
      url: '/api/invitation/invitationInfo',
      method: 'get',
      params: { invitationId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function getStaff(getStaffRequest: GetStaffRequest): Promise<StaffResponse> {
    return await Api.request<StaffResponse>({
      url: '/api/invitation/staff',
      method: 'post',
      payload: getStaffRequest,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function removeInvitation(invitationId: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/invitation/removeInvitation',
      method: 'delete',
      params: { invitationId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function removeStaff(clinicUserId: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/invitation/removeStaff',
      method: 'delete',
      params: { clinicUserId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function inviteToCallBySms(inviteToCallBySmsRequest: InviteToCallBySmsRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/invitation/inviteToCallBySms',
      method: 'put',
      payload: inviteToCallBySmsRequest,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function inviteToCallByEmail(inviteToCallByEmailRequest: InviteToCallByEmailRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/invitation/inviteToCallByEmail',
      method: 'put',
      payload: inviteToCallByEmailRequest,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Subscriptions
  ////////////////////////////////////////////////////////////

  async function getPlan(planCode: string): Promise<GetPlanResponse> {
    return await Api.request<GetPlanResponse>({
      url: '/api/subscriptions/plan',
      method: 'get',
      params: { planCode },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getLicenseUsageInfo(clinicId: string): Promise<LicenseUsageInfoResponse> {
    return await Api.request<LicenseUsageInfoResponse>({
      url: '/api/subscriptions/licenseUsageInfo',
      method: 'get',
      params: { clinicId },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function postCheckoutSubscriptionUpdate(hostedPageId: string): Promise<PostCheckoutSubscriptionsApiResponse> {
    return await Api.request<PostCheckoutSubscriptionsApiResponse>({
      url: '/api/subscriptions/postCheckoutSubscriptionUpdate',
      method: 'post',
      payload: { hostedPageId },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateSubscriptionWithIncreasingAmountOfLicenses(
    payload: UpdateSubscriptionWithIncreasingAmountOfLicensesRequest,
  ): Promise<UpdateSubscriptionWithIncreasingAmountOfLicensesResponse> {
    return await Api.request<UpdateSubscriptionWithIncreasingAmountOfLicensesResponse>({
      url: '/api/subscriptions/updateSubscriptionWithIncreasingAmountOfLicenses',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateSubscriptionWithDecreasingAmountOfLicenses(
    payload: UpdateSubscriptionWithDecreasingAmountOfLicensesRequest,
  ): Promise<SubscriptionsApiResponse> {
    return await Api.request<SubscriptionsApiResponse>({
      url: '/api/subscriptions/updateSubscriptionWithDecreasingAmountOfLicenses',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function cancelCurrentSubscription(): Promise<SubscriptionsApiResponse> {
    return await Api.request<SubscriptionsApiResponse>({
      url: '/api/subscriptions/cancelCurrentSubscription',
      method: 'post',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function createSubscriptionWithHostedPages(
    payload: CreateHostedPageRequest,
  ): Promise<CreateHostedPageResponse> {
    return await Api.request<CreateHostedPageResponse>({
      url: '/api/subscriptions/createSubscriptionWithHostedPages',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateCustomerCurrentCard(
    payload: UpdateCustomerCurrentCardRequest,
  ): Promise<UpdateCustomerCurrentCardResponse> {
    return await Api.request<UpdateCustomerCurrentCardResponse>({
      url: '/api/subscriptions/updateCustomerCurrentCard',
      method: 'post',
      payload: { ...payload, autoCollect: true },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function reactivateCurrentSubscription(): Promise<ReactivateCurrentSubscriptionApiResponse> {
    return await Api.request<ReactivateCurrentSubscriptionApiResponse>({
      url: '/api/subscriptions/reactivateCurrentSubscription',
      method: 'post',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getCoupon(promocode: string): Promise<GetCouponSubscriptionApiResponse> {
    return await Api.request<GetCouponSubscriptionApiResponse>({
      url: '/api/subscriptions/coupon',
      method: 'get',
      params: { couponCode: promocode },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function manageLicense(payload: ManageLicenseRequest): Promise<SubscriptionsApiResponse> {
    return await Api.request<SubscriptionsApiResponse>({
      url: '/api/subscriptions/manageLicense',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function computeSubscriptionProration(
    payload: ComputeSubscriptionProrationRequest,
  ): Promise<ComputeSubscriptionProrationResponse> {
    return await Api.request<ComputeSubscriptionProrationResponse>({
      url: '/api/subscriptions/computeSubscriptionProration',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getSubscriptionInvoices(
    page = 1,
    perPage = 20,
    filterBy = 'Status.All',
  ): Promise<GetSubscriptionInvoicesResponse> {
    return await Api.request<GetSubscriptionInvoicesResponse>({
      url: '/api/subscriptions/invoices',
      method: 'get',
      params: { page: page.toString(), perPage: perPage.toString(), filterBy },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// User
  ////////////////////////////////////////////////////////////

  async function updateUser(updateUserRequest: UpdateUserRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/updateUser',
      method: 'put',
      payload: updateUserRequest,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateProfilePicture(file: File | null): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/updateProfilePicture',
      method: 'put',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      payload: { file },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function sendCodeToPhone(twoFactorMode: TwoFactor, languageCode?: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/sendCodeToPhone',
      method: 'put',
      params: { twoFactorMode, ...(languageCode && { languageCode }) },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function verifyPhoneCode(code: string): Promise<boolean> {
    return await Api.request<boolean>({
      url: '/api/user/verifyPhoneCode',
      method: 'put',
      params: { code },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function checkEmailExists(email: string): Promise<boolean> {
    return await Api.request<boolean>({
      url: '/api/user/checkEmailExists',
      method: 'get',
      params: { email },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getUser(): Promise<ApiUser> {
    return await Api.request<ApiUser>({
      url: '/api/user/getCurrentUser',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function preUpdatePhone(newPhone: string, twoFactorMode: string, languageCode?: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/preUpdatePhone',
      method: 'put',
      payload: { newPhone, twoFactorMode, languageCode },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function updatePhone(newPhone: string, code: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/updatePhone',
      method: 'put',
      payload: { newPhone, code },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function preUpdateSmsPhone(newPhone: string, twoFactorMode: string, languageCode?: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/preUpdateSmsPhone',
      method: 'put',
      payload: { newPhone, twoFactorMode, languageCode },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function updateSmsPhone(newPhone: string, code: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/updateSmsPhone',
      method: 'put',
      payload: { newPhone, code },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function turnOnTwoFactor(): Promise<void> {
    await Api.request<void>({
      url: '/api/user/enableTwoFactor',
      method: 'post',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function turnOffTwoFactor(code: string): Promise<void> {
    await Api.request<string>({
      url: '/api/user/disableTwoFactor',
      method: 'post',
      params: { code },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function changeTwoFactor(twoFactor: TwoFactor): Promise<void> {
    await Api.request<string>({
      url: '/api/user/changeTwoFactor',
      method: 'put',
      params: { twoFactor },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function createBaa(payload: CreateBaaRequest): Promise<any> {
    return await Api.request<any>({
      url: '/api/user/createBaa',
      method: 'post',
      payload,
      responseType: 'blob',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function connectStripe(): Promise<string> {
    return await Api.request<string>({
      url: '/api/acceptPayments/connectStripe',
      method: 'post',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function disconnectStripe(): Promise<void> {
    return await Api.request<void>({
      url: '/api/acceptPayments/disconnectStripe',
      method: 'post',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function allowDisablePayments(payload: AllowDisablePaymentsRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/acceptPayments/allowDisablePayments',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function createPayment(payload: CreatePaymentRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/acceptPayments/createPayment',
      method: 'post',
      payload,
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function cancellPaymentByClient(clientConnectionId: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/acceptPayments/cancellPaymentByClient',
      method: 'post',
      payload: { clientConnectionId },
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function cancellPaymentByProvider(): Promise<void> {
    return await Api.request<void>({
      url: '/api/acceptPayments/cancellPaymentByProvider',
      method: 'post',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Documents
  ////////////////////////////////////////////////////////////

  async function listPersonalDocuments(path: string): Promise<DocumentWithMetadataDto[]> {
    return await Api.request<DocumentWithMetadataDto[]>({
      url: '/api/documents/list',
      method: 'get',
      params: { path },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function listPersonalDocumentsWithMetadata(path: string): Promise<DocumentWithMetadataDto[]> {
    return await Api.request<DocumentWithMetadataDto[]>({
      url: '/api/documents/full-list',
      method: 'get',
      params: { path },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function listClinicDocuments(path: string): Promise<DocumentWithMetadataDto[]> {
    return await Api.request<DocumentWithMetadataDto[]>({
      url: '/api/documents',
      method: 'get',
      params: { path },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function uploadPersonalDocument(file: File, path: string, overwrite = true): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    return await Api.request<string>({
      url: '/api/documents/upload',
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { path },
      payload: formData,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function uploadClinicDocument(file: File, path: string, overwrite = true): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    return await Api.request<string>({
      url: '/api/documents/upload',
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { path },
      payload: formData,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function uploadGuestChatDocument(
    file: File,
    providerId: string,
    connectionId: string,
  ): Promise<{ path: string; fileName: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file);

    return await Api.request<{ path: string; fileName: string; size: number }>({
      url: '/api/documents/chat-upload',
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { providerId, connectionId },
      payload: formData,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function downloadPersonalDocument(path: string, fileName: string): Promise<void> {
    try {
      const response = await Api.request<Blob>({
        url: '/api/documents/download',
        method: 'get',
        params: { path, fileName },
        responseType: 'blob',
      });

      const blob = new Blob([response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const error = e as AxiosError;
      throw toApiError(error);
    }
  }

  async function downloadSharedDocument(fullPath: string, fileName: string): Promise<void> {
    try {
      const response = await Api.request<Blob>({
        url: '/api/documents/shared-download',
        method: 'get',
        params: { path: fullPath }, // 👈 send full blob path
        responseType: 'blob',
      });

      const blob = new Blob([response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const error = e as AxiosError;
      throw toApiError(error);
    }
  }

  async function deletePersonalDocument(path: string, fileName: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/delete',
      method: 'delete',
      params: { path, fileName },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function renamePersonalDocument(path: string, oldName: string, newName: string): Promise<string> {
    return await Api.request<string>({
      url: '/api/documents/rename',
      method: 'put',
      params: { path, oldName, newName },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updatePinnedState(path: string, fileName: string, pinned: boolean): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/pin',
      method: 'put',
      params: { path, fileName, pinned: pinned.toString() },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function markDocumentAsShared(path: string, fileName: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/shared',
      method: 'put',
      params: { path, fileName },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getBlobPath(
    clinicId: string,
    userId: string,
    fileName: string,
    scope: 'Clinic' | 'Personal' = 'Clinic',
  ): Promise<{ id: string }> {
    const params: Record<string, string> = {
      scope,
      clinicId,
      fileName,
    };

    // Only include userId when required
    if (scope === 'Personal') {
      params.userId = userId;
    }

    return await Api.request<{ id: string }>({
      url: '/api/documents/file-id',
      method: 'get',
      params,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getDirectDownloadUrl(path: string, fileName: string): Promise<string> {
    return await Api.request<{ url: string }>({
      url: '/api/documents/direct-url',
      method: 'get',
      params: { path, fileName },
    })
      .then((res) => res.url)
      .catch((e: AxiosError) => {
        throw toApiError(e);
      });
  }

  ////////////////////////////////////////////////////////////
  /// AI Notes
  ////////////////////////////////////////////////////////////

  async function confirmTermsAndConditions(): Promise<void> {
    return await Api.request<void>({
      url: '/api/aiNotes/confirmTermsAndConditions',
      method: 'patch',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getAllAINotesItems(): Promise<GetAllAINotesPurchaseItemsResponse> {
    return await Api.request<GetAllAINotesPurchaseItemsResponse>({
      url: '/api/aiNotes/getAllItems',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function makePurchase(itemId: string): Promise<MakePurchaseResponse> {
    return await Api.request<MakePurchaseResponse>({
      url: '/api/aiNotes/makePurchase',
      method: 'post',
      payload: { itemId },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getAINotesPreselects(): Promise<AINotesPreselectsResponse> {
    return await Api.request<AINotesPreselectsResponse>({
      url: '/api/aiNotes/getAINotesPreselects',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function startRecording(startRecordData: {
    typeOfVisit: string;
    writtenAs: string;
    generateNote: string;
    includeReductedTranscript: boolean;
  }): Promise<void> {
    return await Api.request<void>({
      url: '/api/aiNotes/StartRecording',
      method: 'post',
      payload: startRecordData,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function stopRecording(): Promise<void> {
    return await Api.request<void>({
      url: '/api/aiNotes/StopRecording',
      method: 'post',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getAINotes(page: number, count: number): Promise<GetAINotesResponse> {
    return await Api.request<GetAINotesResponse>({
      url: `/api/aiNotes/AINotes?page=${page}&count=${count}`,
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function downloadNote(aiNote: string, isPdf: boolean): Promise<any> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return await Api.request<any>({
      url: '/api/aiNotes/DownloadAINote',
      method: 'get',
      params: { aiNote, timezone, isPdf: isPdf + '' },
      responseType: 'blob',
    }).catch((e: any) => {
      throw toApiError(e);
    });
  }

  async function processTooShortRecording(aiNote: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/aiNotes/processTooShortRecording',
      method: 'post',
      params: { aiNote },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function processStoppedNotByUserRecording(aiNote: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/aiNotes/processStoppedNotByUserRecording',
      method: 'post',
      params: { aiNote },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Change Role
  ////////////////////////////////////////////////////////////

  async function changeUserRole(payload: { clinicUserId: string; via3UserType: 'Staff' | 'Member' }): Promise<void> {
    return await Api.request<void>({
      url: '/api/user/change-role',
      method: 'post',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Faxing Feature
  ////////////////////////////////////////////////////////////

  async function getSentFaxes(): Promise<any> {
    return await Api.request<any>({
      url: '/api/faxes/sent',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getReceivedFaxes(): Promise<any> {
    return await Api.request<any>({
      url: '/api/faxes/received',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function sendFax(files: File[], faxNumber: string, subject: string): Promise<void> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file); // 🔑 must match backend DTO
    });

    formData.append('faxNumber', faxNumber);
    formData.append('subject', subject);

    await Api.request<void>({
      url: '/api/faxes/send',
      method: 'post',
      payload: formData,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getFaxAccount(): Promise<FaxAccountDto> {
    return await Api.request<FaxAccountDto>({
      url: '/api/faxes/account',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function downloadReceivedFax(faxId: string): Promise<Blob> {
    return await Api.request<Blob>({
      url: `/api/faxes/received/${faxId}/download`,
      method: 'get',
      responseType: 'blob',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function downloadSentFax(faxId: string): Promise<Blob> {
    return await Api.request<Blob>({
      url: `/api/faxes/sent/${faxId}/download`,
      method: 'get',
      responseType: 'blob',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function deleteReceivedFax(faxDetailsId: string): Promise<void> {
    await Api.request({
      method: 'DELETE',
      url: `/api/faxes/received/${faxDetailsId}`,
    });
  }

  async function deleteSentFax(faxId: string): Promise<void> {
    return await Api.request<void>({
      url: `/api/faxes/sent/${encodeURIComponent(faxId)}`,
      method: 'delete',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function resendSentFax(faxId: string): Promise<void> {
    await Api.request<void>({
      url: `/api/faxes/sent/${faxId}/resend`,
      method: 'post',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Create Clinic Folder
  ////////////////////////////////////////////////////////////

  async function createClinicFolder(name: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/folders',
      method: 'post',
      payload: { name },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getClinicStorage() {
    return await Api.request<{ bytes: number; megabytes: number }>({
      method: 'GET',
      url: '/api/documents/clinic-storage',
    });
  }

  async function renameClinicFolder(oldPath: string, newPath: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/folders/rename',
      method: 'put',
      params: { oldPath, newPath },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function deleteClinicFolder(path: string): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/folder',
      method: 'delete',
      params: {
        path,
      },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function getFolderPermissions(folderId: string): Promise<FolderMemberPermissionDto[]> {
    return await Api.request<FolderMemberPermissionDto[]>({
      url: '/api/documents/folders/permissions',
      method: 'get',
      params: { folderId },
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  async function updateFolderPermissions(payload: UpdateFolderPermissionsRequest): Promise<void> {
    return await Api.request<void>({
      url: '/api/documents/folders/permissions',
      method: 'put',
      payload,
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Public Staff for VIA3 Home Page
  ////////////////////////////////////////////////////////////

  // PUBLIC — no auth required
  async function getPublicStaff(): Promise<StaffResponse> {
    return await Api.unauthRequest<StaffResponse>({
      url: '/api/public/staff',
      method: 'get',
    }).catch((e: AxiosError) => {
      throw toApiError(e);
    });
  }

  ////////////////////////////////////////////////////////////
  /// Call History
  ////////////////////////////////////////////////////////////

  async function getCallHistoryPaged(
    startUtc: string,
    endUtc: string,
    hostClinicUserId: string | null,
    offset: number,
    count: number,
  ): Promise<{ items: any[]; totalCount: number }> {
    return await Api.request<{ items: any[]; totalCount: number }>({
      url: '/api/CallHistory/GetCallHistoryPaged',
      method: 'get',
      params: {
        startUtc,
        endUtc,
        offset: offset.toString(), // ✅ stringify
        count: count.toString(), // ✅ stringify
        ...(hostClinicUserId ? { hostClinicUserId } : {}), // ✅ omit when null
      },
    });
  }

  async function getCallHistoryExport(
    startUtc: string,
    endUtc: string,
    hostClinicUserId: string | null,
    clientTimeZone: string,
  ): Promise<Blob> {
    return await Api.request<Blob>({
      url: '/api/CallHistory/GetCallHistoryExport',
      method: 'get',
      params: {
        startUtc,
        endUtc,
        clientTimeZone,
        ...(hostClinicUserId ? { hostClinicUserId } : {}), // ✅ omit when null
      },
      responseType: 'blob',
    });
  }

  async function getCallHistoryHosts(
    startUtc: string,
    endUtc: string,
  ): Promise<{ hostClinicUserId: string; hostName: string }[]> {
    return await Api.request<{ hostClinicUserId: string; hostName: string }[]>({
      url: '/api/CallHistory/GetCallHistoryHosts',
      method: 'get',
      params: {
        startUtc,
        endUtc,
      },
    });
  }

  async function getCallHistoryMinDate(): Promise<string | null> {
    return await Api.request<string | null>({
      url: '/api/CallHistory/GetCallHistoryMinDate',
      method: 'get',
    });
  }

  return {
    register,
    login,
    loginWithVerificationCode,
    refreshToken,
    logout,
    registerSecondAccount,
    confirmEmail,
    resendConfirmEmail,
    sendResetPassword,
    resetPassword,
    updateUserPassword,
    registerUserByInvitation,

    updateCallHistory,
    endCall,
    createRoom,
    getRoomHistory,
    getDiagnosticsToken,
    speedTestDownload,
    speedTestUpload,

    updateClinic,
    updateClinicPicture,
    getClinicsList,
    changeClinic,

    updateClinicUser,
    updateCheckInNotificationPreferences,
    getProfileByLink,
    skipCreateRoom,

    ensureRoomNameIsUnique,

    invite,
    invitationResend,
    invitationDecline,
    invitationAccept,
    invitationInfo,
    getStaff,
    removeStaff,
    removeInvitation,
    inviteToCallBySms,
    inviteToCallByEmail,

    getPlan,
    updateSubscriptionWithIncreasingAmountOfLicenses,
    updateSubscriptionWithDecreasingAmountOfLicenses,
    postCheckoutSubscriptionUpdate,
    getLicenseUsageInfo,
    cancelCurrentSubscription,
    createSubscriptionWithHostedPages,
    updateCustomerCurrentCard,
    reactivateCurrentSubscription,
    getCoupon,
    manageLicense,
    computeSubscriptionProration,
    getSubscriptionInvoices,

    updateUser,
    updateProfilePicture,
    sendCodeToPhone,
    verifyPhoneCode,
    checkEmailExists,
    getUser,
    preUpdatePhone,
    updatePhone,
    preUpdateSmsPhone,
    updateSmsPhone,
    turnOnTwoFactor,
    turnOffTwoFactor,
    changeTwoFactor,
    createBaa,

    connectStripe,
    disconnectStripe,
    allowDisablePayments,
    createPayment,
    cancellPaymentByClient,
    cancellPaymentByProvider,

    listPersonalDocuments,
    listPersonalDocumentsWithMetadata,
    listClinicDocuments,
    uploadPersonalDocument,
    uploadClinicDocument,
    uploadGuestChatDocument,
    downloadPersonalDocument,
    downloadSharedDocument,
    deletePersonalDocument,
    renamePersonalDocument,
    updatePinnedState,
    markDocumentAsShared,
    getBlobPath,
    getDirectDownloadUrl,

    confirmTermsAndConditions,
    getAllAINotesItems,
    makePurchase,
    getAINotesPreselects,
    startRecording,
    stopRecording,
    getAINotes,
    downloadNote,
    processTooShortRecording,
    processStoppedNotByUserRecording,

    changeUserRole,

    getSentFaxes,
    getReceivedFaxes,
    sendFax,
    getFaxAccount,
    downloadReceivedFax,
    downloadSentFax,
    deleteReceivedFax,
    deleteSentFax,
    resendSentFax,
    createClinicFolder,
    renameClinicFolder,
    deleteClinicFolder,
    getFolderPermissions,
    updateFolderPermissions,
    getClinicStorage,

    getPublicStaff,

    getCallHistoryPaged,
    getCallHistoryExport,
    getCallHistoryHosts,
    getCallHistoryMinDate,

    resetStaffPassword,

    startTeleconsentSession,
    submitTeleconsentForm,
    getTeleconsentSettings,
    updateTeleconsentSettings,

    getCustomConsentForms,
    getCustomConsentForm,
    createCustomConsentForm,
    updateCustomConsentForm,
    deleteCustomConsentForm,

    sendTeleconsentConversionRequest,
  };
}
